import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportReactErrorBoundaryError } from "@/lib/runtime-monitoring";

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    void reportReactErrorBoundaryError(error, errorInfo.componentStack ?? undefined);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            background:
              "linear-gradient(180deg, rgb(248, 250, 252) 0%, rgb(241, 245, 249) 100%)",
            color: "rgb(15, 23, 42)",
            fontFamily:
              'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "420px",
              borderRadius: "16px",
              border: "1px solid rgb(226, 232, 240)",
              backgroundColor: "rgba(255, 255, 255, 0.96)",
              padding: "32px",
              boxShadow: "0 20px 45px rgba(15, 23, 42, 0.08)",
            }}
          >
            <p
              style={{
                margin: "0 0 12px",
                fontSize: "12px",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "rgb(71, 85, 105)",
              }}
            >
              Joyride
            </p>
            <h1
              style={{
                margin: "0 0 12px",
                fontSize: "28px",
                lineHeight: 1.1,
              }}
            >
              Something went wrong
            </h1>
            <p
              style={{
                margin: "0 0 24px",
                fontSize: "15px",
                lineHeight: 1.5,
                color: "rgb(51, 65, 85)",
              }}
            >
              The app hit an unexpected error. Reload to try again.
            </p>
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                border: 0,
                borderRadius: "999px",
                backgroundColor: "rgb(15, 23, 42)",
                color: "white",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: 600,
                minHeight: "44px",
                padding: "0 18px",
              }}
            >
              Reload app
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
